<template>
  <div class="signing-checklist-selected-documents-list-item py-2" :class="{ 'not-signable' : isFileOverPages }" data-locator="selected-file-row" :data-name="file.objectToPost.fileName">
    <ClosdFildersIcon
      :document="file"
      :signed-file="file.signedOnClosd"
    />

    <div class="signing-checklist-selected-documents-list-item__file-data ml-2 mr-4">
      <template v-if="hasToBeUploaded">
        <div class="d-flex justify-space-between mb-1">
          <p class="text-text-secondary mb-0 d-flex">
            {{ file.objectToPost.fileName }}
            <SigningChecklistDocumentWarning :file="file" textInline @update:isFileOverPages="handleFileOverPages"/>
          </p>
          <p v-if="file.size" class="mb-0 text-text-tertiary">
            <app-filesize-display :value="file.size" />
          </p>
        </div>
        <v-progress-linear
          :model-value="file.progress"
          height="6"
          bg-color="surface-grey"
          color="surface-info-bold"
          rounded
        />
      </template>
      <template v-else>
        <p class="text-text-secondary mb-0">
          {{ file.objectToPost.fileName }}
        </p>
        <p v-if="file.size" class="mb-0 d-flex text-text-tertiary">
          <app-filesize-display :value="file.size"/>
          <SigningChecklistDocumentWarning :file="file" textInline @update:isFileOverPages="handleFileOverPages"/>
        </p>
      </template>
    </div>

    <div class="signing-checklist-selected-documents-list-item__actions">
      <app-button
        size="small"
        type="icon"
        data-locator="replace-button"
        @click="handleSwapFiles"
      >
        <app-material-icon name="cached" />
      </app-button>
      <app-button
        v-if="canDelete"
        size="small"
        type="icon"
        data-locator="delete-button"
        @click="$emit('deleteFile', file)"
      >
        <app-material-icon name="delete" />
      </app-button>
      <template v-if="hasToBeUploaded">
        <app-material-icon
          v-if="file.status === 'success'"
          name="check_circle"
          color="success"
          filled
        />
        <app-material-icon
          v-if="file.status === 'error'"
          name="warning"
          filled
          color="warning"
        />
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, PropType, ref } from 'vue'
import { useStore } from 'vuex'

import ClosdFildersIcon from '@/common/filders/ClosdFildersIcon.vue'
import SigningChecklistDocumentWarning from '@/project/signing-checklist/SigningChecklistDocumentWarning.vue'
import { SigningChecklistFile } from '@/project/views/SigningChecklistAdd.vue'

const props = defineProps({
  file: {
    type: Object as PropType<SigningChecklistFile>,
    required: true,
  },
  canDelete: {
    type: Boolean,
    default: true,
  },
})

const hasToBeUploaded = computed(() => !!props.file.objectToPost.fileRaw)

const isFileOverPages = ref(false)
const handleFileOverPages = (value: boolean) => {
  isFileOverPages.value = value
}

const store = useStore()

const handleSwapFiles = () => {
  // envelopeId is null when the file is only in add process
  const envelopeId = props.file?.objectToPost?.envelope?.id ?? null;
  const fileToSwap = {
    objectToPost: props.file.objectToPost,
    envelope: {id:envelopeId, file: props.file},
    title: props.file.objectToPost.fileName,
  }
  store.commit('signingChecklist/SET_SWAP_FILES', {
    ids: [props.file.id],
    steps: [fileToSwap],
  })
}
</script>

<style scoped lang="scss">
@import "@/scss/border-tokens";

.signing-checklist-selected-documents-list-item {
  display: flex;
  align-items: center;
  padding-right: 8px;

  &__file-data {
    flex: 1;
  }

  &__actions {
    display: flex;
    column-gap: 12px;

    svg {
      width: 18px;
      height: 18px;
    }
  }

  &:not(:last-of-type):not(.not-signable):has(+ :not(.not-signable)) {
    border-bottom: get-border('border-secondary');
  }
  &.not-signable {
    background-color: rgb(var(--v-theme-surface-warning-subtle));
    border: get-border('border-warning-bold');
    @include border-radius('border-radius-3');
  }
}
</style>
